import Button from 'components/form/Button';
import TextArea from 'components/form/TextArea';
import Modal from 'components/Layout/Modal';
import React, { useEffect, useState } from 'react';
import Label from 'components/form/Label';
import { PassBackground, icons } from 'utils/constants';
import TextInput from 'components/form/TextInput';
import { Formik } from 'formik';
import html2pdf from 'html2pdf.js';
import { useDispatch, useSelector } from 'react-redux';
import { throwError, throwSuccess } from 'store/slices';
import './InstantPassPopup.scss';
import FileUpload from 'components/form/FileUpload';
import { generatePreSignedUrl, getFilenameFromUrl } from 'utils/helpers';
import Loader from 'components/Layout/Loader';

const sampleData = {
  name: 'John Dow',
  date: '25th - 26th November,2025',
  memberid: 'TEST12345',
  conference:
    '44th World Conference on Applied Science, Engineering & Technology (44th WCASET-2023)',
  location: 'India',
  pass_background_file: '',
};

const InstantPassPopup = ({
  onHide,
  handleAPI,
  handleSuccess,
  setHtmlContentData,
  htmlContentData,
  certificateImg,
  instantPassData,
}) => {
  const dispatch = useDispatch();
  const { eventData } = useSelector((state) => ({
    eventData: state.global.eventData,
  }));
  const [htmlContent, setHtmlContent] = useState(htmlContentData);
  const [isForm, setIsForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [bgImg, setBgImg] = useState('');
  const [qrBase64, setQrBase64] = useState('');

  const isVal = true;

  const [initialValues, setInitialValues] = useState({
    name: isVal ? sampleData.name : '',
    date: isVal ? sampleData.date : '',
    memberid: isVal ? sampleData.memberid : '',
    conference: isVal ? sampleData.conference : '',
    location: isVal ? sampleData.location : '',
    pass_background_file: isVal ? sampleData?.pass_background_file : '',
    conference_bg_name: getFilenameFromUrl(''),
    pass_qr_img: qrBase64,
  });

  const convertToBase64 = async (imageUrl) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
    });
  };
  const staticQRUrl = `https://res.cloudinary.com/db4ayioxs/image/upload/v1747650754/uploads/1747650753721-Untitled%201.png.png`;

  const loadStaticQR = async () => {
    try {
      // Replace with your actual static QR image URL
      const response = await fetch(staticQRUrl);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64 = reader.result;
          setQrBase64(base64);
          setInitialValues((prev) => ({
            ...prev,
            pass_qr_img: base64,
          }));
          resolve(base64);
        };
      });
    } catch (error) {
      console.error('Error loading QR image:', error);
      return '';
    }
  };

  const fetchProfile = async (profileName) => {
    const response = await generatePreSignedUrl(profileName, PassBackground);
    if (response) {
      const base64Image = await convertToBase64(response);
      setInitialValues((prev) => ({
        ...prev,
        pass_background_file: base64Image || '',
      }));
      generateHtml({
        ...initialValues,
        pass_background_file: base64Image,
        pass_qr_img: qrBase64,
      });
    }
  };

  useEffect(() => {
    localStorage.setItem('htmlContent', htmlContentData);
    loadStaticQR();
  }, [htmlContentData]);

  useEffect(() => {
    if (certificateImg) {
      fetchProfile(certificateImg);
    }
  }, [certificateImg]);

  const handleSubmit = async () => {
    setIsFormLoading(true);
    const response = await handleAPI(
      htmlContentData,
      htmlContent,
      bgImg,
      qrBase64
    );
    if (response?.status === 200) {
      handleSuccess();
      setHtmlContent('');
      setHtmlContentData('');
      localStorage.removeItem('htmlContent');
    }
    setIsFormLoading(false);
  };

  const handleTextareaChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContentData(newHtml);
    setHtmlContent(newHtml);
    localStorage.setItem('htmlContent', newHtml);
  };

  const generateHtml = (values) => {
    let updatedHtml = localStorage.getItem('htmlContent') || htmlContent;
    if (updatedHtml) {
      const valuesWithQR = {
        ...values,
        pass_qr_img: values.pass_qr_img || qrBase64,
      };

      Object.keys(valuesWithQR)?.forEach((key) => {
        const placeholder = `{{${key}}}`;
        updatedHtml = updatedHtml?.replaceAll(
          placeholder,
          valuesWithQR[key] || ''
        );
      });
      setHtmlContent(updatedHtml);
    }
  };

  const handleDownload = async () => {
    if (!htmlContent) return;
    try {
      setIsLoading(true);
      const container = document.createElement('div');
      container.innerHTML = htmlContent;

      const options = {
        margin: 10,
        filename: 'sample_certificate.pdf',
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'landscape',
        },
      };

      await html2pdf()
        .set(options)
        .from(container)
        .save()
        .then(() => {
          dispatch(throwSuccess('Downloaded successfully!'));
        })
        .catch((err) => {
          dispatch(throwError({ message: err }));
        });
    } catch (error) {
      dispatch(throwError({ message: error }));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!htmlContent) return;

    try {
      setIsPreviewLoading(true);
      const container = document.createElement('div');
      container.innerHTML = htmlContent;

      const options = {
        margin: 10,
        filename: 'sample_certificate.pdf',
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'landscape',
        },
      };

      const pdfBlob = await html2pdf()
        .set(options)
        .from(container)
        .output('blob');

      const previewUrl = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(previewUrl);
    } catch (error) {
      dispatch(throwError({ message: error }));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (isForm && isVal) generateHtml(initialValues);
  }, [isForm, isVal]);

  useEffect(() => {
    const storedHtml = localStorage.getItem('htmlContent');
    if (storedHtml) {
      setHtmlContent(storedHtml);
    }
  }, []);

  return (
    <>
      {pdfPreviewUrl && (
        <Modal
          fullscreen
          largeClose
          onHide={() => {
            setPdfPreviewUrl('');
          }}
        >
          <iframe
            frameBorder="0"
            className="w-100"
            src={`${pdfPreviewUrl}#toolbar=0&navpanes=0`}
            title="description"
            style={{
              width: '100%',
              height: '99%',
            }}
          />
        </Modal>
      )}
      <Modal
        size="lg"
        width={'1320px'}
        onHide={() => {
          onHide();
          setHtmlContent('');
          localStorage.removeItem('htmlContent');
        }}
        titleBesideClose={eventData.event_name}
        titleBesideCloseClassName={'cps-15'}
        isTitleBorder
        isTitleVerticalCenter
        className={'new-certificate-generate-popup'}
      >
        <div className="cps-32 cpt-32 cpe-32 cpb-32">
          <div className="new-certificate-container cmb-28">
            <div className="left-section">
              {isForm ? (
                <Formik initialValues={initialValues} enableReinitialize>
                  {(props) => {
                    const { values, handleChange, setFieldValue } = props;
                    const {
                      name,
                      date,
                      memberid,
                      conference,
                      location,
                      pass_bg_name,
                    } = values;
                    return (
                      <div className="form row">
                        <div className="col-md-6 cmb-20">
                          <TextInput
                            label={'Name'}
                            id="name"
                            value={name}
                            onChange={(e) => {
                              handleChange(e);
                              generateHtml({
                                ...values,
                                [e.target.id]: e.target.value,
                              });
                            }}
                          />
                        </div>
                        <div className="col-md-6 cmb-20">
                          <TextInput
                            label={'Date'}
                            id="date"
                            value={date}
                            onChange={(e) => {
                              handleChange(e);
                              generateHtml({
                                ...values,
                                [e.target.id]: e.target.value,
                              });
                            }}
                          />
                        </div>

                        <div className="col-md-12 cmb-20">
                          <TextInput
                            label={'Conference Name'}
                            id="conference"
                            value={conference}
                            onChange={(e) => {
                              handleChange(e);
                              generateHtml({
                                ...values,
                                [e.target.id]: e.target.value,
                              });
                            }}
                          />
                        </div>
                        <div className="col-md-6 cmb-20">
                          <TextInput
                            label={'Membership ID'}
                            id="memberid"
                            value={memberid}
                            onChange={(e) => {
                              handleChange(e);
                              generateHtml({
                                ...values,
                                [e.target.id]: e.target.value,
                              });
                            }}
                          />
                        </div>

                        <div className="col-md-6 cmb-20">
                          <TextInput
                            label={'Location'}
                            id="location"
                            value={location}
                            onChange={(e) => {
                              handleChange(e);
                              generateHtml({
                                ...values,
                                [e.target.id]: e.target.value,
                              });
                            }}
                          />
                        </div>

                        <FileUpload
                          label="Pass Background Img"
                          id="pass_background_file"
                          fileText={getFilenameFromUrl(pass_bg_name)}
                          onChange={(e) => {
                            const fileName = e.target.fileName;
                            const newBgImg = e.target.value;
                            setBgImg(newBgImg);
                            setFieldValue('pass_bg_name', fileName);
                            setFieldValue('pass_background_file', newBgImg);
                            generateHtml({
                              ...values,
                              pass_background_file: newBgImg,
                              pass_bg_name: fileName,
                            });
                          }}
                          acceptType={['jpg', 'jpeg', 'png']}
                          fileType="image"
                          value={bgImg}
                        />
                      </div>
                    );
                  }}
                </Formik>
              ) : (
                <TextArea
                  label={'HTML Content'}
                  rows={25}
                  isRequired
                  value={htmlContentData}
                  placeholder={'Write your HTML,CSS code here...'}
                  onChange={handleTextareaChange}
                />
              )}
            </div>
            <div className="right-section">
              <Label label={'Preview'} />
              {htmlContent ? (
                <div className="dynamic-preview-section d-flex align-items-center justify-content-center flex-column">
                  <div
                    className="preview-output"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                </div>
              ) : (
                <>
                  <div className="preview-container d-flex align-items-center justify-content-center flex-column">
                    <img
                      src={icons?.PreviewInfo}
                      alt="info"
                      className="cmb-24"
                    />
                    <div className="color-6866 text-24-500-30">
                      Write code to Preview
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <Button
              text="Back"
              btnStyle="light-outline"
              className="h-35 cps-20 cpe-20"
              onClick={() => {
                setIsForm(!isForm);
                localStorage.removeItem('htmlContent');
              }}
              disabled={!isForm}
            />
            <Button
              text={`${isForm ? 'Submit' : 'Proceed Next'}`}
              btnStyle="primary-dark"
              className="h-35 cps-20 cpe-20"
              btnLoading={isForm && isFormLoading}
              onClick={() => {
                if (isForm) {
                  handleSubmit();
                } else {
                  setIsForm(!isForm);
                }
              }}
              disabled={!htmlContent || isLoading}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default InstantPassPopup;
